import React from "react";

import { CRow, CCol } from "@coreui/react";
import BaseListing from "../../BaseListings";
import { wtApiCall } from "../../helpers/commons/ApiWrapper";
import { lzNotifyErrorMessageFromResult } from "../../helpers/commons/notifications";
import moment from "moment";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "./index.css";
import WidgetTimer from "../widgets/WidgetTimer";
import Loading from "src/helpers/commons/loader";

class VisitorsManagementDetailDashboard extends BaseListing {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      visits_completed: 0,
      visits_pending: 0,
      future_visits: 0,
      fromDate: moment().format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
      data: null,
      timer: 60,
      visitorCountOvertimeChartData: {
        xAxisCategories: [],
        seriesData: [],
      },
    };
  }

  componentDidMount() {
    this.loadList();
  }

  handleTimer(value) {
    if (value === 0) {
      window.location.reload(false);
    }

    this.setState({ timer: value });
  }

  loadList() {
    const { fromDate, toDate } = this.state;
    this.setState({ isLoading: true });

    wtApiCall(
      "visitors-management-detail-dashboard",
      {
        from_date: fromDate,
        to_date: toDate,
      },
      "get",
      (result) => {
        if (typeof result !== "undefined") {
          this.setState({
            data: result.data,
            timer: 60,
          });
        }
        this.setState({ isLoading: false });
      },
      (result) => {
        lzNotifyErrorMessageFromResult(result);
        this.setState({ isLoading: false });
      }
    );
  }

  changeValue(key, val) {
    if (key === "fromDate") {
      this.setState({
        fromDate: val,
      });
    } else if (key === "toDate") {
      this.setState({
        toDate: val,
      });
    }
  }

  renderInsightCard = ({ icon, alt, title, value, subtitle }) => (
    <div className="visitors-dashboard-stat-card">
      <div className="visitors-dashboard-stat-icon-wrap">
        <img className="visitors-dashboard-stat-icon" src={icon} alt={alt} />
      </div>
      <div className="visitors-dashboard-stat-content">
        <div className="visitors-dashboard-stat-title">{title}</div>
        <div className="visitors-dashboard-stat-value">{value || "No data"}</div>
        {subtitle ? (
          <div className="visitors-dashboard-stat-subtitle">{subtitle}</div>
        ) : null}
      </div>
    </div>
  );

  render() {
    const { isLoading, data, timer } = this.state;

    if (isLoading) {
      return Loading;
    }

    const visitorData = {
      visitorTrend: data ? data.date_wise_visitor_grouped_data : {},
      reasonsForVisit: {
        data: data ? data.reasons_for_visit : [],
      },
      employeeVisitorCount: data ? data.employee_wise_visitor_count : {},
      mostVisitedFirms: data ? data.most_visited_firms : {},
    };

    const visitorTrend = {
      chart: {
        height: 300,
        type: "column",
      },
      title: {
        text: "Visitor Trend Chart",
        align: "center",
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        categories: visitorData.visitorTrend.categories,
        title: {
          text: "Dates",
        },
      },
      yAxis: {
        min: 0,
        title: {
          text: "Number of Visitors",
        },
      },
      plotOptions: {
        column: {
          pointPadding: 0.18,
          borderWidth: 0,
          borderRadius: 6,
        },
      },
      series: [
        {
          name: "Visitors",
          color: "#1c6bb8",
          data: visitorData.visitorTrend.data,
        },
      ],
    };

    const mostVisitedFirms = {
      chart: {
        type: "line",
        height: 300,
      },
      title: {
        text: "Most Visited Firms",
        align: "center",
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        categories: visitorData.mostVisitedFirms.categories,
        title: {
          text: "Firm Name",
        },
      },
      yAxis: {
        title: {
          text: "Number of Visits",
        },
      },
      series: [
        {
          name: "No of Visits",
          color: "#2f8c52",
          data: visitorData.mostVisitedFirms.data,
        },
      ],
    };

    const frequentVisitorPieChart = {
      chart: {
        type: "pie",
        height: 320,
      },
      credits: {
        enabled: false,
      },
      title: {
        text: "Visitor Frequency Chart",
        align: "center",
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: "pointer",
          dataLabels: {
            enabled: true,
            format: "<b>{point.name}</b>: {point.y}",
          },
        },
      },
      series: [
        {
          name: "Number of Visits",
          data: data ? data.most_frequent_visitors : [],
        },
      ],
    };

    const employeeVisitorCountOptions = {
      title: {
        text: "Employee-wise Visitor Count",
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        categories: visitorData.employeeVisitorCount.categories,
      },
      yAxis: {
        title: {
          text: "Number of Visitors",
        },
      },
      series: [
        {
          name: "Visitors",
          color: "#cf8d1c",
          data: visitorData.employeeVisitorCount.visitorCounts,
        },
      ],
      chart: {
        type: "bar",
        height: 320,
      },
    };

    const insightCards = data
      ? [
          {
            icon: "avatars/dashboard-icons/visitor.png",
            alt: "visitor.png",
            title: "Total Visitors Today",
            value: data.total_visitors_today,
          },
          {
            icon: "avatars/dashboard-icons/visitor_this_week.png",
            alt: "visitor_this_week.png",
            title: "Total Visitors This Week",
            value: data.total_visitors_this_week,
          },
          {
            icon: "avatars/dashboard-icons/checked_in.png",
            alt: "checked_in.png",
            title: "Total Visitors This Month",
            value: data.total_visitors_this_month,
          },
          {
            icon: "avatars/dashboard-icons/busiest_time.png",
            alt: "busiest_time.png",
            title: "Busiest Time of Day",
            value: data.busiest_time_of_day ? data.busiest_time_of_day.time : "No data",
            subtitle: data.busiest_time_of_day
              ? `${data.busiest_time_of_day.count} visits`
              : "",
          },
          {
            icon: "avatars/dashboard-icons/most_visited_employee.png",
            alt: "most_visited_employee.png",
            title: "Most Visited Employee",
            value: data.most_visited_employee
              ? data.most_visited_employee.employee_name
              : "No data",
            subtitle: data.most_visited_employee
              ? `${data.most_visited_employee.count} visits`
              : "",
          },
          {
            icon: "avatars/dashboard-icons/checked_out.png",
            alt: "checked_out.png",
            title: "Average Time Of Visitor",
            value: data.average_time_of_visitor,
          },
        ]
      : [];

    return (
      <div
        className="container-fluid visitors-dashboard-page"
        style={{ pointerEvents: isLoading ? "none" : "auto" }}
      >
        {data !== null && (
          <CRow className="visitors-dashboard-shell">
            <CCol md="12">
              <CRow className="visitors-dashboard-page-header">
                <CCol md="12">
                  <div className="visitors-dashboard-hero">
                    <div className="visitors-dashboard-eyebrow">Reception Analytics</div>
                    <h4 className="visitors-dashboard-heading">Visitors Dashboard</h4>
                  </div>
                </CCol>
              </CRow>

              <CRow className="visitors-dashboard-top-row">
                <CCol lg="3">
                  <div className="visitors-dashboard-stats-grid">
                    {insightCards.map((card, index) => (
                      <div
                        key={`${card.title}-${index}`}
                        className="ceo-dashboard-parent-box"
                      >
                        {this.renderInsightCard(card)}
                      </div>
                    ))}
                  </div>
                </CCol>
                <CCol lg="9">
                  <div className="visitors-dashboard-chart-card">
                    <div className="visitors-dashboard-chart-card-inner">
                      <HighchartsReact highcharts={Highcharts} options={visitorTrend} />
                    </div>
                  </div>

                  <div className="visitors-dashboard-chart-card visitors-dashboard-chart-card-spaced">
                    <div className="visitors-dashboard-chart-card-inner">
                      <HighchartsReact highcharts={Highcharts} options={mostVisitedFirms} />
                    </div>
                  </div>
                </CCol>
              </CRow>

              <div className="visitors-dashboard-timer-panel visitors-dashboard-timer-panel-bottom">
                <WidgetTimer
                  timer={timer}
                  setTimer={(value) => this.handleTimer(value)}
                />
              </div>

              <CRow className="visitors-dashboard-bottom-row">
                <CCol md="6">
                  <div className="visitors-dashboard-chart-card">
                    <div className="visitors-dashboard-chart-card-inner">
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={frequentVisitorPieChart}
                      />
                    </div>
                  </div>
                </CCol>
                <CCol md="6">
                  <div className="visitors-dashboard-chart-card">
                    <div className="visitors-dashboard-chart-card-inner">
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={employeeVisitorCountOptions}
                      />
                    </div>
                  </div>
                </CCol>
              </CRow>
            </CCol>
          </CRow>
        )}
      </div>
    );
  }
}

export default VisitorsManagementDetailDashboard;
